import { z } from "zod";

/**
 * `classes` domain DTOs (Master Spec §33/§34, Phase 12). A class is a
 * teacher-owned roster (`classes`/`class_members` — see `database/
 * schema/classes.ts`). Enrollment itself lives in the sibling
 * `class-members` domain (`class-members.ts`) — §33 lists `classes` and
 * `class-members` as two separate API domains, not one nested resource,
 * and this file follows that split rather than inventing a nested
 * `/classes/:id/members` shape this codebase has never used anywhere.
 */
export const CreateClassRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
});
export type CreateClassRequest = z.infer<typeof CreateClassRequestSchema>;

/**
 * `memberCount` is included so a teacher's class list doesn't need a
 * second round trip just to show roster size; `role` tells a `GET /`
 * caller whether they're looking at a class they teach or one they're
 * enrolled in as a student (the same endpoint serves both — see
 * `classes.service.ts`'s `listClassDTOsForUser`).
 */
export const ClassDTOSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  teacherId: z.string().uuid(),
  memberCount: z.number().int().nonnegative(),
  role: z.enum(["teaching", "enrolled"]),
});
export type ClassDTO = z.infer<typeof ClassDTOSchema>;

export const CreateClassResponseSchema = z.object({
  class: ClassDTOSchema,
});
export type CreateClassResponse = z.infer<typeof CreateClassResponseSchema>;

export const ListClassesResponseSchema = z.object({
  classes: z.array(ClassDTOSchema),
});
export type ListClassesResponse = z.infer<typeof ListClassesResponseSchema>;
